import type { GradoConclusione, PhaseSevenDto, ProgrammaDto } from '@bk/contracts';
import { HelpRows, ScrapTable } from './Fase6';
import { PhaseFrame, usePhase } from './fasi-comune';
import type { PhasePageProps } from './fasi-pagine';

/**
 * La fase 7 «Programmi e piattaforme, in tre famiglie» (docs/mockup/percorso-sinapsi/10-fase7; contratto n. 1 versione
 * 2.4, art. 8, fase 7; ADR 0177): i numeri in testa, A le piattaforme pubbliche con le identità digitali, B i programmi
 * dei processi con la prova d'uso o «probabile», C gli strumenti quotidiani, le domande superate, lo scarto, le cose che
 * aiuterebbero, le domande, «Parliamone», «Non va bene» e «Confermo la fase». La cornice è quella della fase 6.
 *
 * Registrata in `fasi-pagine.tsx` per il passo «software»; i dati vengono da `GET /onboarding/fase/7`.
 */

/** La pagina della fase 7, registrata in `fasi-pagine.tsx` per il passo «software». */
export function Fase7Page({ next, onNext, sealing, stopped = false }: PhasePageProps) {
  const { q, reload, confirm, redo } = usePhase<PhaseSevenDto>(7);
  return (
    <PhaseFrame
      d={q.data}
      error={q.error}
      eyebrow="Fase 7 · Programmi e piattaforme"
      title={(d) => `Con quali programmi lavora ${d.azienda ?? 'la tua azienda'}`}
      working="Preparo programmi e piattaforme"
      questionsIntro={QUESTIONS_INTRO7}
      scrapIntro=""
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      beforeQuestions={(d) => (
        <>
          <ScrapTable list={d.scarto_fase} intro="Programmi doppi o mancanti, e lavori fatti a mano che un programma del settore fa." />
          <HelpRows list={d.aiuterebbero} />
        </>
      )}
    >
      {(d) => <Fase7Body d={d} />}
    </PhaseFrame>
  );
}

export const QUESTIONS_INTRO7 = 'Una domanda per ogni categoria dei processi che nessuna prova d’uso copre, nell’ordine del peso su processi e obblighi.';

const gradeChip = (g: GradoConclusione) => <span className={`sn-chip ${g === 'certo' ? 'T' : g === 'probabile' ? 'C' : 'plain'}`}>{g === 'certo' ? 'Certo' : g === 'probabile' ? 'Probabile' : 'Possibile'}</span>;

function Product({ p }: { p: ProgrammaDto }) {
  if (!p.prodotti.length) return <span className="sn-src gold">Nessuna prova d’uso</span>;
  return (
    <>
      {p.prodotti.map((x, k) => (
        <span key={k} style={{ display: 'block' }}>
          {x.nome ? (
            <>
              {x.nome}
              <span className="sub">
                {x.prova_url ? (
                  <a href={x.prova_url} target="_blank" rel="noreferrer" className="sn-src violet">
                    Prova d’uso: {x.prova_uso}
                  </a>
                ) : (
                  <span className="sn-src violet">Prova d’uso: {x.prova_uso}</span>
                )}
              </span>
            </>
          ) : x.prova_uso ? (
            <>
              {x.tipo} <span className="sn-src ok">· {x.prova_uso}</span>
            </>
          ) : (
            <>
              <span className="sn-src gold">Probabile</span>
              <span className="sub">{x.tipo}</span>
            </>
          )}
        </span>
      ))}
    </>
  );
}

/** Il corpo della pagina della fase 7, sotto la testa, i bloccanti e «cosa è cambiato» della cornice comune. */
export function Fase7Body({ d }: { d: PhaseSevenDto }) {
  const c = d.conti;
  return (
    <>
      <section className="sn-figs">
        <div className="sn-fig">
          <p className="k">A · Piattaforme</p>
          <p className="v">{c.piattaforme}</p>
          <p className="s">
            {c.obbligatorie} obbligatorie · {c.facoltative} {c.facoltative === 1 ? 'facoltativa' : 'facoltative'}
          </p>
        </div>
        <div className="sn-fig">
          <p className="k">B · Programmi dei processi</p>
          <p className="v">{c.programmi}</p>
          <p className="s">{c.con_prova} con prova d’uso</p>
        </div>
        <div className="sn-fig">
          <p className="k">C · Strumenti quotidiani</p>
          <p className="v">{c.strumenti}</p>
          <p className="s" />
        </div>
        <div className={`sn-fig ${c.attivita && c.attivita_con_programma / c.attivita < 0.9 ? 'gold' : ''}`}>
          <p className="k">Attività con un programma</p>
          <p className="v">
            {c.attivita_con_programma}/{c.attivita}
          </p>
          <p className="s">Nei loro processi</p>
        </div>
      </section>

      <section className="sn-panel flush" style={{ gap: 0 }}>
        <div className="sn-ph">
          <h2 className="sn-h2">A · Piattaforme pubbliche</h2>
        </div>
        <table className="sn-tbl wide">
          <thead>
            <tr>
              <th>Piattaforma, ente, norma</th>
              <th style={{ width: 150 }}>Cadenza</th>
              <th style={{ width: 220 }}>Accesso · chi accede</th>
              <th style={{ width: 170 }}>Sanzione</th>
            </tr>
          </thead>
          <tbody>
            {d.piattaforme.map((p) => (
              <tr key={p.id}>
                <td>
                  <b>{p.nome}</b>
                  {p.nome_ufficiale && p.nome_ufficiale !== p.nome && <> ({p.nome_ufficiale})</>}
                  {!p.obbligatoria && <> · facoltativa</>}
                  <span className="sub">{p.obbligatoria ? [p.ente, p.norma].filter(Boolean).join(' · ') : (p.nota ?? p.ente)}</span>
                </td>
                <td>{p.cadenza ?? '—'}</td>
                <td>{p.chi_accede === 'intermediario' ? `intermediario con delega${p.intermediario ? `: ${p.intermediario}` : ''}` : `${p.accesso ?? '—'} · l’azienda`}</td>
                <td>{p.sanzione ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '12px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {d.piattaforme.length > 0 && d.piattaforme.every((p) => p.grado === 'certo') && <p className="sn-note">Tutte certe: ognuna viene da un obbligo della fase 6, con la norma e il fatto.</p>}
          {d.identita.length > 0 && (
            <>
              <p className="lbl" style={{ margin: 0 }}>
                IDENTITÀ DIGITALI E FIRME DIGITALI
              </p>
              <div className="sn-chips">
                {d.identita.map((i) => (
                  <span key={i.nome} className="sn-chip plain" style={{ textTransform: 'uppercase' }}>
                    {i.nome}
                    {i.fonte ? ` · ${i.fonte}` : ''}
                  </span>
                ))}
                <span className="sn-dash">{d.identita_chi}</span>
              </div>
            </>
          )}
          <p className="sn-note">Per ognuna: dati che riceve e passa, processi in cui serve, dove gira (in cloud dell’ente) e «responsabile: da assegnare».</p>
        </div>
      </section>

      <section className="sn-panel flush" style={{ gap: 0 }}>
        <div className="sn-ph">
          <h2 className="sn-h2">B · Programmi dei processi</h2>
        </div>
        <p className="sn-note" style={{ padding: '0 24px 10px' }}>
          Una categoria per ogni processo della fase 4 che il settore fa con un programma.{d.senza_programma ? ` ${d.senza_programma}` : ''}
        </p>
        <table className="sn-tbl wide">
          <thead>
            <tr>
              <th>Categoria e processo</th>
              <th style={{ width: 230 }}>Prodotto</th>
              <th>Dati che riceve e passa</th>
              <th style={{ width: 200 }}>Dove gira</th>
            </tr>
          </thead>
          <tbody>
            {d.programmi.map((p) => (
              <tr key={p.id}>
                <td>
                  <b>{p.categoria}</b>
                  {p.processo && <span className="sub">processo: {p.processo}</span>}
                </td>
                <td>
                  <Product p={p} />
                </td>
                <td>{p.dati ?? '—'}</td>
                <td>{p.dove_gira.testo}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sn-chips" style={{ padding: '12px 24px' }}>
          <span className="sn-dash">Responsabile di ogni programma: da assegnare · chi lo usa per il suo processo</span>
          <span className="sn-note">Grado: certo con la prova d’uso, probabile senza (prodotto scritto come tipo, senza marchi).</span>
        </div>
      </section>

      <section className="sn-panel flush" style={{ gap: 0 }}>
        <div className="sn-ph">
          <h2 className="sn-h2">C · Strumenti quotidiani</h2>
        </div>
        <table className="sn-tbl wide">
          <thead>
            <tr>
              <th>Categoria</th>
              <th>Chi li usa</th>
              <th>Dove gira</th>
              <th style={{ width: 160 }}>Fonte</th>
              <th style={{ width: 110 }}>Grado</th>
            </tr>
          </thead>
          <tbody>
            {d.strumenti.map((p) => (
              <tr key={p.id}>
                <td>{p.categoria}</td>
                <td>{p.chi_lo_usa ?? '—'}</td>
                <td>{p.dove_gira.testo}</td>
                <td className="f on">
                  <span className="sn-src">{p.dal_registro ?? p.fonte ?? '—'}</span>
                </td>
                <td>{gradeChip(p.grado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {d.superate.map((x, k) => (
        <section key={k} className="sn-panel" style={{ gap: 8, padding: '18px 24px' }}>
          <span className="lbl">UNA DOMANDA DELLA FASE {x.fase} È SUPERATA</span>
          <p style={{ fontSize: 14 }}>
            «{x.domanda}» non serve più: {x.perche}
          </p>
        </section>
      ))}
    </>
  );
}
